import {Injectable} from '@angular/core';
import {HttpClient, HttpHeaders} from '@angular/common/http';
import {finalize} from 'rxjs/internal/operators';
import {CommonDataService} from './common-data.service';
import {CommonService} from './common.service';
import {ServiceDetails} from './service-details';
import {InvoiceService} from '../invoices/invoice.service';
import {BehaviorSubject, Observable} from 'rxjs';
import {CookieService} from 'ngx-cookie-service';
import {LinkedAccount} from './linked-account';
import {LinkAccountService} from '../link-account/link-account.service';
import {ActivatedRouteSnapshot, Router, RouterStateSnapshot} from '@angular/router';
import {Constants} from './constants';
import {OtpService} from '../otp/otp.service';

@Injectable()
export class AuthService {
  _initialLoading: BehaviorSubject<boolean> = new BehaviorSubject(true);

  authenticated = false;
  unauthorisedMessage: string = null;
  navigatedUrl: string = null;
  initialLoading$: Observable<boolean> = this._initialLoading.asObservable();


  constructor(private http: HttpClient,
              private commonDataService: CommonDataService,
              private commonService: CommonService,
              private invoiceService: InvoiceService,
              private cookieService: CookieService,
              private linkAccountService: LinkAccountService,
              private otpService: OtpService,
              private router: Router) {
  }

  /*loginAfterSettingNewPassword(brand, username, password): Observable<any> {
    return this.http.get<any>('/api/loginWithNewPassword?brand=' + brand + "&username=" + username + "&password=" + password);
  }*/

  switchAccount(): void {
    this.commonService.getSwitchAccountRedirectUrl().subscribe(result => {
      this.commonService.clearSelectedService();
      this.logOut(true, result);
    }, err => {
      console.error(err);
    });
  }

  switchToMigratedAccount(): void {
    (window as any).location.replace('/api/switch/switchToMigratedAccount');
  }

  logOut(redirect?: boolean, redirectUrl?: string) {
    this.http.post('logout', {}).pipe(finalize(() => {
      this.cookieService.deleteAll('/');
      this.authenticated = false;
      this.setUnauthorizedMessage('');
      this.commonDataService.loadSetUpData();
      this.navigatedUrl = null;
      this.otpService.passed = false;
      if (redirect) {
        if (redirectUrl) {
          (window as any).location.replace(redirectUrl);
        } else {
          this.commonService.redirectToLoginPageByBrand();
        }
      }
    })).subscribe();
  }

  authenticate(credentials, callback) {
    const headers = new HttpHeaders(credentials ? {
      authorization: 'Basic ' + btoa(credentials.username + ':' + credentials.password)
    } : {});

    this.http.get('user', {headers: headers}).subscribe(response => {
      if (response['userId']) {
        this.authenticated = true;
        this.commonDataService.user.billingAccountId = response['billingAccountId'];
        this.commonDataService.user.userId = response['userId'];

        let brand = response['tenant'];
        if (brand) {
          this.commonDataService.brand = brand;
        }
        this.commonDataService.csrMode = (response['csrName'] != null && 'non-csr' !== response['csrName']);

        let otpPassed = response['otpPassed'];
        let lockedAccountExpiryInMinutes: number = response['lockedAccountExpiryInMinutes'];
        if (otpPassed || this.commonDataService.csrMode) {
          this.otpService.passed = true;
        } else if (lockedAccountExpiryInMinutes > 0) {
          this.otpService.minutesToUnlock = lockedAccountExpiryInMinutes;
          this.otpService.setErrorMessage('LOCKEDACCOUNT');
        }
      }
      this.initialLoading = false;
      return callback && callback();
    });
  }

  // changeVirtualPhoneServiceName() {
  //   const virtualPhoneIndex = this.commonDataService.serviceDetailsList.findIndex(serviceDetails =>
  //     serviceDetails.serviceKey.indexOf('_Voice_') !== -1
  //     && serviceDetails.serviceTypeKey === 'Consumer Voice'
  //   );
  //   if (virtualPhoneIndex !== -1) {
  //     this.commonDataService.serviceDetailsList[virtualPhoneIndex].serviceName = 'Virtual Phone';
  //   }
  // }

  setUnauthorizedMessage(failure) {
    if ('login' == failure) {
      this.unauthorisedMessage = 'Invalid username/password';
    } else if ('user' == failure) {
      this.unauthorisedMessage = 'User details not found';
    } else if ('service' == failure) {
      this.unauthorisedMessage = 'Service details not found';
    } else {
      this.unauthorisedMessage = '';
    }
  }

  get initialLoading() {
    return this._initialLoading.value;
  }

  set initialLoading(value) {
    this._initialLoading.next(value);
  }

  getUrl(url) {
    for (let parentPath of Constants.REDIRECT_TO_PARENT_URLS) {
      let parentUrl = '/' + parentPath;
      if (url.startsWith(parentUrl)) {
        // pages with user selection dependency so just redirect to parent
        return parentUrl;
      }
    }
    return url;
  }

  hasMultipleServices(): boolean {
    return this.commonDataService.serviceDetailsList?.length > 1;
  }

  canActivateNavigationCallback(route: ActivatedRouteSnapshot, state: RouterStateSnapshot) {
    let url = '';
    if (this.authenticated) {
      if (!this.hasMultipleServices) {
        url = this.getUrl(state.url);
      } else {
        url = this.getUrl(state.url);
        const broadbandServices = this.commonDataService.getBroadbandServices();

        if (state.url.startsWith(`/${Constants.MY_PLAN_PATH}`)) {
          if (route.url.length === 3 && route.url[1].path === 'change') {
            const serviceId = route.url[route.url.length - 1].path;
            const matchingService = this.commonDataService.serviceDetailsList.find(service => serviceId === service.serviceId);
            if (matchingService) {
              this.commonService.selectService(matchingService, false);
            }
            url = `/${Constants.MY_PLAN_PATH}/change`;
          } else if (broadbandServices.length === 1 && this.commonDataService.getMobileServices().length === 0) {
            this.commonService.selectService(broadbandServices[0], false);
          }
        } else if (localStorage.length > 0 && localStorage.getItem(Constants.LOCAL_STORAGE_SELECTED_SERVICE_KEY) != null) {
          let selectedSvcInLocalStorage: ServiceDetails = JSON.parse(localStorage.getItem(Constants.LOCAL_STORAGE_SELECTED_SERVICE_KEY));
          for (let svc of this.commonDataService.serviceDetailsList) {
            if (svc.serviceId === selectedSvcInLocalStorage.serviceId) {
              this.commonService.selectService(svc);
              break;
            }
          }
        }

        if (!this.commonDataService.selectedServiceDetails) {
          for (let parentPath of Constants.NEED_SERVICE_SELECTION_URLS) {
            // needs user selection
            let parentUrl = '/' + parentPath;
            if (state.url.startsWith(parentUrl)) {
              url = '/' + Constants.SELECT_SERVICE_PATH;
              break;
            }
          }

          this.commonService.clearSelectedService();
        }
      }

      this.navigatedUrl = url;
      this.router.navigateByUrl(url);
      return true;

    } else {
      this.setUnauthorizedMessage('');
      this.commonService.redirectToLoginPageByBrand();
      return false;
    }
  }
}
